import { BaseManager } from '../core/baseManager';
import { windowScope } from '../window/windowScope';

interface NavigationState {
    closeMobNav: ((removeDim: boolean) => void) | null;
    megaIsInUse: boolean;
    navHoverCounter: number;
    inMobile: boolean;
}

export class NavigationManager extends BaseManager {
    private state: NavigationState;
    private body: HTMLElement;

    constructor() {
        super('NavigationManager');
        this.body = document.querySelector('body') as HTMLElement;
        this.state = {
            closeMobNav: null,
            megaIsInUse: false,
            navHoverCounter: 0,
            inMobile: window.matchMedia('(max-width: 991px)').matches
        };
    }

    public initialize(): void {
        super.initialize();
        this.bootstrapNavHover();
        this.bootstrapMobileNavAlterations();
        this.checkMegaMenuEdge();
        this.injectTopBarButtonIntoMobNav();
    }

    public checkMegaMenuEdge(): void {
        // For right aligning the mega panes on desktop when possible
        // Get all mega containers
        const megaContainers = document.querySelectorAll('.navbar--mega .mega-container');
        // Get viewport width
        const viewportWidth = window.innerWidth;

        megaContainers.forEach(container => {
            // Find the parent nav-item element
            const parentNavItem = container.closest('.nav-item') as HTMLElement;
            if (parentNavItem !== null) {
                // Reset the element
                parentNavItem.classList.remove('pane-close-to-edge');
                // Get the container's bounding rectangle
                const rect = container.getBoundingClientRect();
                // Calculate distance from right edge
                const distanceFromRight = viewportWidth - (rect.left + rect.width);
                // Check if distance is less than 18px (accounting for scroll bar width)
                if (distanceFromRight < 18) {
                    parentNavItem.classList.add('pane-close-to-edge');
                } else {
                    parentNavItem.classList.remove('pane-close-to-edge');
                }
            }
        });
    }

    public bootstrapNavHover(resizing?: string): void {
        if (resizing === "resizing") {
            // Replace the entire nav structure in order to remove bound events on resize
            const navbarBasic = document.querySelector('.navbar--mega') as HTMLElement;
            if (navbarBasic != null) {
                navbarBasic.outerHTML = navbarBasic.outerHTML;
            }
        }

        if (!this.state.inMobile) { // desk nav (but can appear on touch devices)
            const navHover = (that: HTMLElement, elem: HTMLElement, ind: number) => {
                let el_link = that.querySelector('a[data-sbx-toggle="custom"]') as HTMLElement;
                if (el_link != null) {
                    let nextEl = el_link.nextElementSibling as HTMLElement;
                    el_link.classList.add('show');
                    nextEl.classList.add('show');
                    if (!this.body.classList.contains('dim')) {
                        this.body.classList.add('dim', 'dim--invisi');
                    }
                    this.state.megaIsInUse = true; // fallback of below to ensure this value is true when users is wafting mouse over all top level nav items quickly
                    setTimeout(() => { // runs slightly after the "megaIsInUse = false" in the mouseleave event so we know if user has gone from one pane to another
                        this.state.megaIsInUse = true;
                    }, 20);
                    this.state.navHoverCounter++;
                }
            };

            document.querySelectorAll('.navbar--mega .nav-item.dropdown').forEach((item, i) => {
                const htmlItem = item as HTMLElement;

                htmlItem.addEventListener('mouseenter', (e: MouseEvent) => {
                    navHover(htmlItem, htmlItem, i);
                });

                htmlItem.addEventListener('mouseleave', () => {
                    let el_link = htmlItem.querySelector('a[data-sbx-toggle="custom"]') as HTMLElement;
                    if (el_link != null) {
                        let nextEl = el_link.nextElementSibling as HTMLElement;
                        el_link.classList.remove('show');
                        nextEl.classList.remove('show');
                        if (this.body.classList.contains('dim--invisi')) {
                            this.body.classList.remove('dim', 'dim--invisi');
                        }
                        this.state.megaIsInUse = false;
                        if (this.state.navHoverCounter > 0) { // add the mega-in-use class if going from one pane to another (avoids animation running on more than one pane)
                            this.body.classList.add('mega-in-use');
                        }
                        setTimeout(() => { // remove the mega-in-use class when pane collapsed without expanding another pane (resets the animation for next time nav opened)
                            if (!this.state.megaIsInUse) {
                                this.body.classList.remove('mega-in-use');
                                this.state.navHoverCounter = 0;
                            }
                        }, 700); // it takes about 700 for the animation to finish via css
                    }
                });

                htmlItem.addEventListener('touchstart', (e: TouchEvent) => { // large touch devices should trigger on touchstart rather than mouseover
                    const target = e.target as HTMLElement;
                    if (target.getAttribute('data-sbx-toggle') !== null) { // clicking on top level nav rather than anything within it i.e. the mega pane
                        e.preventDefault(); // prevent the default href link behavior on touch devices so that user can see the dropdown
                        const curTargetAnchor = htmlItem.querySelectorAll('.show');
                        if (curTargetAnchor.length) {
                            curTargetAnchor.forEach((el) => {
                                el.classList.remove('show'); // close this item
                            });
                            //this.body.classList.remove('dim');
                        } else { // close all items and open this item
                            this.state.closeMobNav?.(true);
                            navHover(htmlItem, htmlItem, i);
                            //this.body.classList.add('dim');
                        }
                    }
                });
            });
        } else { // mobile nav
            document.querySelectorAll('a[data-sbx-toggle="custom"]').forEach((e) => {
                const htmlElement = e as HTMLElement;
                htmlElement.addEventListener('click', (el: MouseEvent) => {
                    el.preventDefault(); // prevent the default href link behavior on mobile devices so that user can see the dropdown
                    const elTarget = el.target as HTMLElement;
                    let nextEl = elTarget.nextElementSibling as HTMLElement;
                    if (elTarget.classList.contains('show')) {
                        htmlElement.parentElement?.classList.remove('dropdown-menu-shown');
                        this.body.classList.remove('a-dropdown-menu-shown');
                        document.querySelectorAll('.navbar .navbar-nav .show').forEach((elem) => {
                            elem.classList.remove('show');
                        });
                    } else {
                        htmlElement.parentElement?.classList.add('dropdown-menu-shown');
                        this.body.classList.add('a-dropdown-menu-shown');
                        document.querySelectorAll('.navbar .navbar-nav .show').forEach((elem) => {
                            elem.classList.remove('show');
                        });
                        elTarget.classList.add('show');
                        nextEl.classList.add('show');
                    }
                });
            });
        }
    }

    public bootstrapMobileNavAlterations(): void {
        const burgerIcon = document.querySelector('#main-header .navbar-toggler') as HTMLElement;
        if (burgerIcon != null) {
            // close mob nav on click away
            windowScope.closeMobNav = (removeDim: boolean) => {
                burgerIcon.classList.add('collapsed');
                const navbarCollapse = document.querySelector('#main-header .navbar-collapse') as HTMLElement;
                navbarCollapse?.classList.remove('show');
                document.querySelectorAll('.has-megamenu .show').forEach((e) => {
                    e.classList.remove('show');
                });
                this.body.classList.remove('mob-nav-open');
                if (removeDim) {
                    this.body.classList.remove('dim');
                }
            };
            this.state.closeMobNav = windowScope.closeMobNav;

            // add classname to body to show when mobile nav is open
            burgerIcon.addEventListener('click', () => {
                if (burgerIcon.classList.contains('collapsed')) {
                    document.body.classList.remove('mob-nav-open', 'dim');
                }
                else {
                    document.body.classList.add('mob-nav-open', 'dim');
                    windowScope.enableAutocomplete();
                }
            });
        }
    }

    public injectTopBarButtonIntoMobNav(): void {
        const existingButton = document.querySelector('.sbx-top-bar--end .btn');
        const placeholder = document.getElementById('mobMenuBtnPlaceholder');

        if (existingButton && placeholder) {
            placeholder.replaceWith(existingButton.cloneNode(true));
        }
    }

    private lastDimensions = { width: window.innerWidth, height: window.innerHeight };

    public onResize(): void {
        const current = { width: window.innerWidth, height: window.innerHeight };
        if (current.width === this.lastDimensions.width && current.height === this.lastDimensions.height) return; // only do stuff if dimentions actually changed
        this.lastDimensions = current;

        this.state.inMobile = window.matchMedia('(max-width: 991px)').matches;
        this.bootstrapNavHover("resizing"); // ok to resize because html is replaced on resize
        this.bootstrapMobileNavAlterations(); // ok to resize because html is replaced on resize in bootstrapNavHover()
        this.checkMegaMenuEdge();
    }

    public scrollToTop(smooth: boolean = true): void {
        window.scrollTo({
            top: 0,
            behavior: smooth ? 'smooth' : 'auto'
        });
    }

    // Getter for accessing mobile state
    public get isMobile(): boolean {
        return this.state.inMobile;
    }
}
